export const REALTIME_PROTOCOL_VERSION = 1;
export const REALTIME_SUBPROTOCOL = "cars-realtime-v1";
export const MAX_REALTIME_MESSAGE_BYTES = 16 * 1024;
export const MAX_RECORD_SUBSCRIPTIONS = 64;
export const MAX_TRACK_ID = 1_000_000;
function isPlainObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function hasOnlyKeys(value, keys) {
    const allowed = new Set(keys);
    return Object.keys(value).every((key) => allowed.has(key));
}
function isSafeTrackId(value) {
    return (Number.isSafeInteger(value) &&
        value > 0 &&
        value <= MAX_TRACK_ID);
}
function isValidRulesetId(value) {
    return (typeof value === "string" && /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/.test(value));
}
function isSectorTimes(value) {
    return (Array.isArray(value) &&
        value.length === 3 &&
        value.every((part) => Number.isSafeInteger(part) && part > 0));
}
function isRecord(value) {
    if (!isPlainObject(value))
        return false;
    if (!hasOnlyKeys(value, [
        "trackId",
        "rulesetId",
        "revision",
        "timeMs",
        "sectorTimesMs",
        "carId",
        "playerDisplayName",
        "replayId",
        "achievedAt",
    ]))
        return false;
    return (isSafeTrackId(value.trackId) &&
        isValidRulesetId(value.rulesetId) &&
        Number.isSafeInteger(value.revision) &&
        value.revision > 0 &&
        Number.isSafeInteger(value.timeMs) &&
        value.timeMs > 0 &&
        isSectorTimes(value.sectorTimesMs) &&
        isSafeTrackId(value.carId) &&
        typeof value.playerDisplayName === "string" &&
        value.playerDisplayName.length > 0 &&
        value.playerDisplayName.length <= 128 &&
        typeof value.replayId === "string" &&
        /^[0-9a-f-]{36}$/i.test(value.replayId) &&
        typeof value.achievedAt === "string" &&
        !Number.isNaN(Date.parse(value.achievedAt)));
}
function isTrackIds(value) {
    return (Array.isArray(value) &&
        value.length <= MAX_RECORD_SUBSCRIPTIONS &&
        value.every(isSafeTrackId) &&
        new Set(value).size === value.length);
}
export function parseClientMessage(value) {
    if (!isPlainObject(value) ||
        value.v !== REALTIME_PROTOCOL_VERSION ||
        typeof value.type !== "string")
        return null;
    if (value.type === "auth" &&
        hasOnlyKeys(value, ["v", "type", "token"]) &&
        typeof value.token === "string" &&
        /^[A-Za-z0-9_-]{32,128}$/.test(value.token))
        return { v: 1, type: "auth", token: value.token };
    if (value.type === "subscribe-records" &&
        hasOnlyKeys(value, ["v", "type", "trackIds", "rulesetId"]) &&
        isTrackIds(value.trackIds) &&
        isValidRulesetId(value.rulesetId))
        return {
            v: 1,
            type: "subscribe-records",
            trackIds: value.trackIds,
            rulesetId: value.rulesetId,
        };
    if (value.type === "unsubscribe-records" &&
        hasOnlyKeys(value, ["v", "type", "trackIds"]) &&
        isTrackIds(value.trackIds))
        return { v: 1, type: "unsubscribe-records", trackIds: value.trackIds };
    return null;
}
export function parseServerMessage(value) {
    if (!isPlainObject(value) ||
        value.v !== REALTIME_PROTOCOL_VERSION ||
        typeof value.type !== "string")
        return null;
    if (value.type === "ready" &&
        hasOnlyKeys(value, ["v", "type", "connectionId", "serverTimeMs"]) &&
        typeof value.connectionId === "string" &&
        value.connectionId.length > 0 &&
        Number.isSafeInteger(value.serverTimeMs))
        return {
            v: 1,
            type: "ready",
            connectionId: value.connectionId,
            serverTimeMs: value.serverTimeMs,
        };
    if (value.type === "record-snapshot" &&
        hasOnlyKeys(value, ["v", "type", "records"]) &&
        Array.isArray(value.records) &&
        value.records.length <= MAX_RECORD_SUBSCRIPTIONS &&
        value.records.every(isRecord))
        return { v: 1, type: "record-snapshot", records: value.records };
    if (value.type === "global-record-updated" &&
        hasOnlyKeys(value, ["v", "type", "record"]) &&
        isRecord(value.record))
        return { v: 1, type: "global-record-updated", record: value.record };
    if (value.type === "error" &&
        hasOnlyKeys(value, ["v", "type", "code", "message"]) &&
        typeof value.code === "string" &&
        /^[a-z0-9_-]{1,64}$/.test(value.code) &&
        typeof value.message === "string" &&
        value.message.length <= 256)
        return { v: 1, type: "error", code: value.code, message: value.message };
    return null;
}
export function encodeRealtimeMessage(message) {
    const encoded = JSON.stringify(message);
    if (new TextEncoder().encode(encoded).byteLength > MAX_REALTIME_MESSAGE_BYTES)
        throw new Error("Realtime message exceeds maximum size");
    return encoded;
}
